import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { getMyAccess } from "../lib/clientAccess";
import { ClientAssignmentContext } from "../lib/clientAssignmentContext";
import AssessmentDetailPage from "./AssessmentDetailPage";
import SmartImproveDetailPage from "./SmartImproveDetailPage";

/**
 * Client read-only evaluation view. Content-only: renders INSIDE the shared
 * application shell (ConsultantLayout) so the sidebar stays visible when the
 * client navigates between the evaluations list and an evaluation. Reuses the
 * consultant detail pages in gated read-only mode via ClientAssignmentContext.
 */
export default function ClientEvaluationView({ module }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | ok | denied
  const [versionId, setVersionId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const acc = await getMyAccess(module, id);
        if (cancelled) return;
        setVersionId(acc.version_id || null);
        // Staff may preview; a client must hold an active grant.
        if (acc.granted || !acc.read_only) setStatus("ok");
        else setStatus("denied");
      } catch {
        if (!cancelled) setStatus("denied");
      }
    })();
    return () => { cancelled = true; };
  }, [module, id]);

  if (status === "checking") {
    return (
      <p className="text-sm text-[#64748B]" data-testid="client-evaluation-loading">
        Loading evaluation…
      </p>
    );
  }
  if (status === "denied") {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-sm p-8 max-w-md text-center mx-auto" data-testid="client-evaluation-denied">
        <ShieldCheck className="w-8 h-8 text-[#B91C1C] mx-auto" />
        <h1 className="mt-3 text-lg font-bold text-[#0F172A]">Access not available</h1>
        <p className="mt-2 text-sm text-[#64748B]">
          You do not have access to this evaluation, or it is no longer shared with your account.
        </p>
        <button
          onClick={() => navigate("/transformation/evaluations")}
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#025979] hover:text-[#03738C]"
        >
          <ArrowLeft className="w-4 h-4" /> Back to My Evaluations
        </button>
      </div>
    );
  }

  const ctx = {
    readOnly: true,
    module,
    assignmentId: id,
    versionId,
    backTo: "/transformation/evaluations",
  };

  return (
    <ClientAssignmentContext.Provider value={ctx}>
      {module === "smartevaluate" ? <AssessmentDetailPage /> : <SmartImproveDetailPage />}
    </ClientAssignmentContext.Provider>
  );
}
